import React, { useEffect, useState } from "react";
import { DataTableColumn } from "../../../../components/common/datatable/DataTableTypes";
import { createClickableLink } from "../../../../components/common/datatable/DataTableUtils";
import { SearchSelectWithList } from "../../../../components/common/datatable/filter/SearchSelectWrapper";
import { formatAsCurrency } from "../../../../helpers/utils/TextUtils";
import { formatValueAsDateAndTime } from "../../../../helpers/utils/DateUtils";
import { SearchDatePickerWrapper } from "../../../../components/common/datatable/filter/SearchDatePickerWrapper";
import Breadcrumb from "../../../../components/common/Breadcrumbs";
import { DataTable } from "../../../../components/common/datatable/DataTable";
import { useGetAllTenantsQuery } from "../../../customer/tenantApi";
import { useLazyGetAllCustomerByTenantQuery } from "../../../customer/customerApi";
import {useLazyExportRevenueReportDataQuery, useSearchRevenueReportApiQuery} from "../report";
import { useGetAllStatesQuery } from "../../../data/states/statesApi";
import { useLazyGetAllCountyByStateAbbrQuery } from "../../../data/counties/countiesApi";
import { useLazyGetBranchByCustomerIdQuery } from "../../../customer/branchContactApi";
import ReportType from "./ReportType";
import { getCurrentMonthStartDate } from "../../../../helpers/utils/Utils";
import {useFindUserSettingsByGroupNameQuery} from "../../../../components/common/userSetting/userSettingsApi";
import {useUtilDataQuery} from "../../../profile/profileApi";


const RevenueReport = () => {

    const [triggerExportReportDataQuery] = useLazyExportRevenueReportDataQuery();
    const [type, setType] = useState('REVENUE_REPORT');
    const { data: userSetting } = useFindUserSettingsByGroupNameQuery("Report Revenue");
    const tenantDataApi = useGetAllTenantsQuery("");
    const [getAllCustomerTenantApi, getAllCustomerTenantApiResponse] = useLazyGetAllCustomerByTenantQuery();
    const [getAllCountyByStateAbbrApi, getAllCountyByStateAbbrApiResponse] = useLazyGetAllCountyByStateAbbrQuery()
    const stateDataApi = useGetAllStatesQuery("");
    const [branchDataApi, branchDataApiResponse] = useLazyGetBranchByCustomerIdQuery();
    const formattedStartDate = getCurrentMonthStartDate();
    const {data: utilDataQuery} = useUtilDataQuery("");

    const [crumbs] = useState([
        { content: "Home", url: "/home" },
        { content: "Revenue Report", url: "" }]
    )

    useEffect(() => {
        let tenant = utilDataQuery?.customerLevel === 'SYS' ? "" : utilDataQuery?.tenant;
        if (tenant) {
            getAllCustomerTenantApi({ tenantName: tenant });
        }
        }, [utilDataQuery?.customerLevel, utilDataQuery?.tenant]);


    useEffect(() => {
        document.title = 'Revenue Report';
    }, []);

    const columns: DataTableColumn[] = [
        {
            label: "Order No.",
            accessor: "orderNumber",
            overrideSortColumn: "customer_order_number",
            visibility: { hasShowSetting: false },
            cell: (props: any) => {
                let path = "/orders/" + props.row.original.orderId;
                return createClickableLink(path, props.value, userSetting, false, "", ["ORDR-R"]);
            }
        },
        {
            label: "Tenant",
            accessor: "tenant",
            visibility: { showByDefault: false, hasShowSetting: false, disallowedCustomerLevels: ["TENANT"], canColumnShow: false },
            filterOptions: [{
                dependentColumns: ["Customer"],
                filterComponent: SearchSelectWithList(tenantDataApi?.data, "name", "name", "Select Tenant...", false, "tenant"),
                overrideFilterAccessor: "tenant",
                overrideFilterLabel: "Tenant",
            }],
        },
        {
            label: "Customer",
            accessor: "childCustomer",
            visibility: { hasShowSetting: false,  disallowedCustomerLevels: ["CUSTOMER"] },
            canSort: false,
            cell: (props: any) => {
                return <span>{props.value}</span>;
            },
            filterOptions: [{
                dependentColumns: ["Branch(es)"],
                filterComponent: SearchSelectWithList(getAllCustomerTenantApiResponse?.data, (customer: { customerName: any; customerNumber: any; }) => `${customer.customerName} (${customer.customerNumber})`, "id", "Select Customer...", false, "customerId"),
                onDependencyUpdated: (value: any) => {
                    getAllCustomerTenantApi({ tenantName: value });
                },
                overrideFilterAccessor: "customerId",
            }],
        },

        {
            label: "State", accessor: "state", canSort: false, filterOptions: [{
                dependentColumns: ["County"],
                filterComponent: SearchSelectWithList(stateDataApi?.data, "stateName", "stateAbbreviation", "Select State...", false, "state"),
            }],
            visibility: { hasShowSetting: false }
        },
        {
            label: "County", accessor: "county", canSort: false,
            filterOptions: [{
                filterComponent: SearchSelectWithList(getAllCountyByStateAbbrApiResponse?.data, "countyName", "id", "Select counties...", true, "counties"),
                onDependencyUpdated: (value: any) => {
                    getAllCountyByStateAbbrApi(value)
                },
                overrideFilterAccessor: "counties",
                overrideFilterLabel: "Counties",
            }]
        },

        {
            label: "Bill to Customer",
            accessor: "billToCustomer",
            visibility: { hasShowSetting: false },
            canSort: false,
            cell: (props: any) => {
                return <span>{props.value}</span>;
            },
            filterOptions: [{
                filterComponent: SearchSelectWithList(getAllCustomerTenantApiResponse?.data, (customer: { customerName: any; customerNumber: any; }) => `${customer.customerName} (${customer.customerNumber})`, "id", "Select Customer...", false, "billToCustomer"),
                onDependencyUpdated: (value: any) => {
                    getAllCustomerTenantApi({ tenantName: value });
                },
                overrideFilterAccessor: "billToCustomer",
            }],
        },
        {
            label: "Branch(es)",
            accessor: "branch",
            visibility: { hasShowSetting: false },
            canSort: false,
            cell: (props: { value: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; }) => {
                return <span>{props.value}</span>;
            },
            filterOptions: [{
                filterComponent: SearchSelectWithList(branchDataApiResponse?.data?.data, "name", "id", "Select Branch(es)...", true, "branchIds"),
                onDependencyUpdated: (value: any) => {
                    branchDataApi(value)
                },
                overrideFilterAccessor: "branchIds",
            }]
        },

        {
            label: "Is NTP",
            accessor: "productIsNtp",
            canSort: false,
            visibility: { hasShowSetting: false },
            cell: (props: { value: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; }) => {
                return <span>{props.value}</span>;
            },
        },

        {
            label: "Price", accessor: "price", overrideSortColumn: "order_status", canSort: false,
            cell: (props: { value: any; }) => <span>{formatAsCurrency(props.value)}</span>,
        },

        {
            label: "Included Tax", accessor: "includedTax", overrideSortColumn: "base_price", canSort: false,
            cell: (props: { value: any; }) => <span>{formatAsCurrency(props.value)}</span>,
        },

        {
            label: "1st Delivered Date", accessor: "firstDeliveredDate", overrideSortColumn: "delivered_date",
            canSort: false,
            cell: (props: { row: { original: { firstDeliveredDate: any; }; }; }) => {
                return formatValueAsDateAndTime(props.row.original.firstDeliveredDate, 'MM/DD/YY h:mm a');
            },
            filterOptions: [{
                filterComponent: <SearchDatePickerWrapper accessor={"deliveredDateFrom"} />,
                overrideFilterAccessor: "deliveredDateFrom",
                overrideFilterLabel: "Delivered Date - From",
            }, {
                filterComponent: <SearchDatePickerWrapper accessor={"deliveredDateTo"} />,
                overrideFilterAccessor: "deliveredDateTo",
                overrideFilterLabel: "Delivered Date - To",
            }]

        },

        {
            label: "Paid Amount", accessor: "paidAmount", overrideSortColumn: "amount", canSort: false,
            cell: (props: { value: any; }) => <span>{formatAsCurrency(props.value)}</span>,

        },
        {
            label: "Paid Date", accessor: "paidDate", overrideSortColumn: "creation_date", canSort: false,
            cell: (props: { value: any; }) => {
                return formatValueAsDateAndTime(props.value, 'MM/DD/YY h:mm a');
            },
        }
    ];

    const customFilter = {
        deliveredDateFrom: formattedStartDate
    }

    return (
        <>
            <div className="user-container px-4 py-2">
                <div className="flex justify-between">
                    <Breadcrumb crumbs={crumbs} />
                </div>
                <ReportType type={type} setType={setType} />
                <DataTable
                    columns={columns}
                    actions={{
                        fetchAction: useSearchRevenueReportApiQuery,
                        exportAction: triggerExportReportDataQuery
                    }}
                 
                    defaultFilter={
                        {
                            ...customFilter,
                            "sortOrder": "ASCENDING",
                            "pageNumber": 0,
                            "pageSize": "15",
                            "sort": "creationDate"
                        }
                    }
                    userSettingGroup={"Report Revenue"}
                    basePermission={"REVREPO"}
                />
            </div>
            <div />

        </>
    )
}

export default RevenueReport;